import React, { useState } from 'react'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography,
} from '@material-ui/core'
import { toast } from 'react-toastify'

import { MultiSelector } from '../../../components'
import { Organization } from '../../../models'
import { VendorPerformanceDateFilters } from '../../VendorPerformance/VendorPerformanceDateFilters'
import {
    DEFAULT_VENDOR_PERFORMANCE_PRESET,
    VendorPerformancePreset,
    getDefaultDateRange,
    getPresetRange,
    isDateRangeReady,
    isDateRangeValid,
} from '../../VendorPerformance/datePresets'
import { useVendorPerformanceReports } from '../../../hooks/useVendorPerformanceReports'

interface Props {
    open: boolean
    onClose: () => void
    organizations: Organization[]
}

const today = () => {
    const value = new Date()
    value.setHours(0, 0, 0, 0)
    return value.toISOString().slice(0, 10)
}

export const GenerateVendorPerformanceReportDialog = ({
    open,
    onClose,
    organizations,
}: Props) => {
    const defaultRange = getDefaultDateRange()
    const [preset, setPreset] = useState<VendorPerformancePreset | null>(
        DEFAULT_VENDOR_PERFORMANCE_PRESET,
    )
    const [startDate, setStartDate] = useState(defaultRange.startDate ?? '')
    const [endDate, setEndDate] = useState(defaultRange.endDate ?? '')
    const [includeAllOrganizations, setIncludeAllOrganizations] = useState(true)
    const [organizationIds, setOrganizationIds] = useState<number[]>([])
    const { generateReport, isGenerating } = useVendorPerformanceReports(false)

    const rangeReady = isDateRangeReady(startDate, endDate)
    const rangeValid = isDateRangeValid(startDate, endDate)
    const endNotInFuture = !endDate || endDate <= today()
    const orgsValid = includeAllOrganizations || organizationIds.length > 0
    const canSubmit =
        rangeReady && rangeValid && endNotInFuture && orgsValid && !isGenerating

    const resetAndClose = () => {
        const nextRange = getDefaultDateRange()
        setPreset(DEFAULT_VENDOR_PERFORMANCE_PRESET)
        setStartDate(nextRange.startDate ?? '')
        setEndDate(nextRange.endDate ?? '')
        setIncludeAllOrganizations(true)
        setOrganizationIds([])
        onClose()
    }

    const handleGenerate = async () => {
        try {
            await generateReport({
                ...(startDate ? { start_date: startDate } : {}),
                ...(endDate ? { end_date: endDate } : {}),
                ...(includeAllOrganizations
                    ? {}
                    : { organization_ids: organizationIds }),
            })
            toast.success('Vendor analytics report started.')
            resetAndClose()
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Unable to start vendor analytics report.',
            )
        }
    }

    return (
        <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="sm">
            <DialogTitle>Generate vendor analytics report</DialogTitle>
            <DialogContent>
                <VendorPerformanceDateFilters
                    preset={preset}
                    startDate={startDate}
                    endDate={endDate}
                    onPresetChange={(nextPreset) => {
                        const range = getPresetRange(nextPreset)
                        setPreset(nextPreset)
                        setStartDate(range.startDate ?? '')
                        setEndDate(range.endDate ?? '')
                    }}
                    onStartDateChange={(value) => {
                        setPreset(null)
                        setStartDate(value)
                    }}
                    onEndDateChange={(value) => {
                        setPreset(null)
                        setEndDate(value)
                    }}
                />
                {!rangeValid && (
                    <Typography color="error">
                        Start date must be on or before end date.
                    </Typography>
                )}
                {!endNotInFuture && (
                    <Typography color="error">
                        End date cannot be after today.
                    </Typography>
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeAllOrganizations}
                            onChange={(event) =>
                                setIncludeAllOrganizations(event.target.checked)
                            }
                            color="primary"
                        />
                    }
                    label="All organizations"
                />
                {!includeAllOrganizations && (
                    <MultiSelector
                        label="Organizations"
                        keyIdentifier="vendor-performance-orgs"
                        options={organizations}
                        selectedOptions={organizationIds}
                        onChange={setOrganizationIds}
                        variant="chips"
                        style={{ formControl: { minWidth: '100%' } }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={resetAndClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={!canSubmit}
                >
                    {isGenerating ? 'Starting…' : 'Generate'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
